import { useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

const SLIDES = [
  {
    title: "Discover who you are",
    body: "Our psychometric assessment uncovers your unique traits — the natural strengths and tendencies that make you, you. Understanding yourself is the first step to choosing a path that fits.",
    accent: "from-indigo-500 to-violet-600",
    icon: (
      <svg viewBox="0 0 48 48" className="w-14 h-14" fill="none">
        <circle cx="24" cy="20" r="10" stroke="white" strokeWidth="2.5" />
        <path d="M10 40c0-7.7 6.3-14 14-14s14 6.3 14 14" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Explore your future",
    body: "Browse career paths aligned with your traits and interests. Follow structured learning journeys built around real subjects, universities, and career routes — all tailored to your curriculum.",
    accent: "from-teal-500 to-emerald-600",
    icon: (
      <svg viewBox="0 0 48 48" className="w-14 h-14" fill="none">
        <path d="M8 36 L24 12 L40 36" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 28h20" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="12" r="3" fill="white" fillOpacity={0.4} />
      </svg>
    ),
  },
  {
    title: "Grow every day",
    body: "Set goals, log activities, earn XP, and track your growth over time. Your AI companion is always on hand to answer questions and keep you on track — at your own pace.",
    accent: "from-amber-500 to-orange-600",
    icon: (
      <svg viewBox="0 0 48 48" className="w-14 h-14" fill="none">
        <path d="M8 34 L16 22 L24 28 L34 14 L40 20" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="8" cy="34" r="2.5" fill="white" />
        <circle cx="40" cy="20" r="2.5" fill="white" />
      </svg>
    ),
  },
]

export function IntroStep({ onNext }) {
  const [slide, setSlide] = useState(0)
  const current = SLIDES[slide]
  const isLast = slide === SLIDES.length - 1

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-black text-slate-900">What you can do here</h2>
        <p className="text-sm text-slate-500">A quick tour of what EarlyScape is all about.</p>
      </div>

      {/* Slide card */}
      <div className={`rounded-3xl bg-linear-to-br ${current.accent} p-6 flex flex-col items-center gap-4 text-center min-h-52`}>
        {current.icon}
        <h3 className="text-xl font-black text-white">{current.title}</h3>
        <p className="text-white/80 text-sm leading-relaxed">{current.body}</p>
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-indigo-600" : "w-2 bg-slate-200"}`}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        {slide > 0 && (
          <button
            onClick={() => setSlide((s) => s - 1)}
            className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-700 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        )}
        <button
          onClick={isLast ? onNext : () => setSlide((s) => s + 1)}
          className="flex-1 flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-95"
        >
          {isLast ? "Start My Journey" : "Next"}
          {!isLast && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  )
}
